// Copyright (c) StrangeBee 2024

import { useCurrentUser } from '@Queries/userQueries';
import { ADMIN_ORG_ID, RouteType } from '@Types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { UserOutput } from 'thehive-sdk';

export const getAccessibleRoutes = (
    userData: UserOutput,
    routes: RouteType[],
    params?: Record<string, string>,
): RouteType[] => {
    const isAdmin = userData && userData.organisation === ADMIN_ORG_ID;
    const currentSpace = isAdmin ? 'admin' : 'user';

    return routes?.filter(({ space, permissionNeeded, displayRoute }) => {
        if (!space?.includes(currentSpace)) {
            return false;
        }

        if (['function', 'boolean'].includes(typeof displayRoute)) {
            const displayValue =
                typeof displayRoute === 'function' ? displayRoute({ userData, isAdmin, params }) : displayRoute;
            if (!displayValue) {
                return false;
            }
        }

        if (permissionNeeded) {
            if (!userData) return false;
            const permissionValue = permissionNeeded.some((permissions) => {
                if (Array.isArray(permissions)) {
                    return permissions.every((permission) => (userData.permissions as string[]).includes(permission));
                }
                return (userData.permissions as string[]).includes(permissions);
            });

            if (!permissionValue) {
                return false;
            }
        }

        return true;
    });
};

export const useGetAccessibleRoutes = (routes: RouteType[]): RouteType[] => {
    const { data: currentUserData } = useCurrentUser();
    const params = useParams<Record<string, string>>();

    const accessibleRoutes = useMemo(() => {
        if (currentUserData && routes) {
            return getAccessibleRoutes(currentUserData, routes, params || {});
        }
        return [];
    }, [currentUserData, routes, params]);

    return accessibleRoutes;
};
